import { FC, useMemo } from 'react';
import styles from './index.module.scss';
import { Space, Table } from '@bfe/moly';
import { ColumnDef } from '@tanstack/react-table';
import { CoinCard } from '@/components/coin-card';
import {
  env,
  formatKMB,
  formatTrend1,
  gt,
  lt,
  openLink,
  replaceBybitToLocationHost,
} from '@web3/base-utils';
import cls from 'classnames';
import { useFm, useScreenType, useParams } from '@web3/base-hooks';

import BtcSvg from '@/assets/imgs/BTC-DARK.svg';

interface IProps {
  title?: string;
  bgColor?: string;
  color?: string;
  list: InscriptionType[];
}

export type InscriptionType = {
  projectName: string;
  chainName: string;
  latestPrice: string;
  totalVolume: string;
  changePercent: string;
};

export const InscriptionList: FC<IProps> = ({ list }) => {
  const { locale } = useParams();
  const fm = useFm();
  const { isMobile } = useScreenType();

  //✅ GOOD: This will not cause an infinite loop of re-renders
  // because `columns` is a stable reference
  const columns: ColumnDef<InscriptionType>[] = useMemo(
    () => [
      {
        accessorKey: 'projectName',
        header: () => (
          <div className="pl-[0px] w-[100px]">{fm('projectName')}</div>
        ),
        cell: ({ row }) => {
          const tickName: string = row.getValue('projectName');

          return (
            <Space className="pl-[0px]">
              <CoinCard
                coin={tickName}
                iconUrl={replaceBybitToLocationHost(row.original.icon)}
                chain={isMobile ? undefined : ''}
                size={isMobile ? 'small' : 'large'}
              />
            </Space>
          );
        },
      },
      {
        accessorKey: 'chainName',
        header: () => <div className="pl-[0px] w-[80px]">{fm('network')}</div>,
        cell: () => (
          <div className={cls('pl-[0px]', styles.chainName)}>
            <BtcSvg className={styles.chainNameIcon} /> <span>BTC</span>
          </div>
        ),
      },
      {
        accessorKey: 'latestPrice',
        header: () => (
          <div className="pl-[0px] w-[80px]">{fm('latestPrice1')}</div>
        ),
        cell: ({ row }) => {
          const latestPrice: string = row.getValue('latestPrice');
          const price = latestPrice ? `$${formatKMB(latestPrice)}` : '--';
          return <div className={cls('pl-[0px]')}>{price}</div>;
        },
      },
      {
        accessorKey: 'totalVolume',
        header: () => (
          <div className="pl-[0px] w-[80px]">{fm('tradeVolume')}</div>
        ),
        cell: ({ row }) => {
          const totalVolume: string = row.getValue('totalVolume');
          const price = totalVolume ? `$${formatKMB(totalVolume)}` : '--';
          return <div className={cls('pl-[0px]')}>{price}</div>;
        },
      },
      {
        accessorKey: 'changePercent',
        header: () => (
          <div className="pr-[0px]  text-right">{fm('change24H')}</div>
        ),
        cell: ({ row }) => {
          const data: string = row.getValue('changePercent');
          const change = formatTrend1(data);
          const isPos = gt(data, 0);
          const isNeg = lt(data, 0);
          return (
            <div
              className={cls('pr-[0px] text-right', {
                green: isPos,
                red: isNeg,
              })}
            >
              {change}
            </div>
          );
        },
      },
    ],
    []
  );

  if (isMobile) {
    columns.splice(1, 1);
  }

  const onRowClick = (
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    rowId: string
  ) => {
    const url = `/${locale}/web3/inscription/project-detail?tick=${list[Number(rowId)].projectName}`;

    if (isMobile) {
      openLink(url);
      return;
    }
    window.open(url);
  };

  return (
    <div className={styles.inscriptionList}>
      <Table
        columns={columns}
        data={list}
        isLite={false}
        // showRowSelection={false}
        rowSplitStyle="none"
        cellSpaceStyle="large"
        onRowClick={onRowClick}
      />
    </div>
  );
};
